import { useEffect, useState } from "react";

import {
    ListContainer,
    ListContext,
    Page,
    PageRoute,
    SearchBar,
    Stack,
    buildPath,
    buildTemplatePath,
    pn,
} from "@compass/core";

import { ListFooter } from "components";
import {
    Permission,
    ClassListDto,
    ClassListFilter,
    ClassListResource,
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import produce from "immer";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { ClassListPageHeader } from "../ClassListPage/ClassListPageHeader";
import { ClassTable } from "./ClassTable";
import { ClassFilter } from "../ClassListFilter";
import { ClassBulkEditPage } from "../ClassBulkEditPage/ClassBulkEditPage";

const routeName = "classes";

export const ClassListPageRoute: PageRoute = {
    handle: "Classes",
    templatePath: buildTemplatePath(routeName),
    getPath: () => buildPath(routeName),
};

export const ClassListPage = () => {
    const ROOT_PERMISSION: Permission = Permission.SystemUtility;

    const [listItems, setListItems] = useState<ClassListDto[]>([]);
    const { listFilter, setListFilter } = useInitListFilter<ClassListFilter>({
        sortObjects: [{ property: pn<ClassListDto>("name"), isDesc: false }],
    });
    const [initListFilter] = useState<ClassListFilter>(listFilter);
    const [total, setTotal] = useState<number>();
    const [resource, setResource] = useState<ClassListResource>({});
    const [searchFocused, setSearchFocused] = useState<boolean>(false);
    const [isBulkEditDialogOpen, setIsBulkEditDialogOpen] = useState<boolean>(false);

    const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod, selectId, selectedIds, setSelectedIds, } =
        useList({
            listFilter: listFilter,
            setListFilter: setListFilter,
            fetchList: (filter) => {
                ApiService.classes.getList(filter).then((response) => {
                    setListItems(response);
                });
            },
            fetchTotal: (filter) => {
                setTotal(undefined);
                ApiService.classes.getListTotal(filter).then((response) => {
                    setTotal(response);
                });
            },
        });

    useEffect(() => {
        loadList(listFilter);
        ApiService.classes.getListResource().then((response) => {
            setResource(response);
        });
    }, []);

    const getIdsMethods = () => ApiService.classes.getIds(listFilter);

    const listFilterNode = useListFilter<ClassListFilter>({
        listFilter: listFilter,
        initListFilter: initListFilter,
        onApply: (filter) => loadList(filter, { resetPage: true }),
        inputNode: (filter, setFilter) => (
            <ClassFilter
                filter={filter.classFilter || {}}
                setFilter={(subFilter) =>
                    setFilter(
                        produce(filter, (draft) => {
                            draft.classFilter = subFilter;
                        })
                    )
                }
            />
        ),
    });

    return (
        <>
            <Page
                header={
                    <ClassListPageHeader
                        rootPermission={ROOT_PERMISSION}
                        handle={ClassListPageRoute.handle}
                    />
                }
            >
                <ListContainer
                    fullHeight
                    header={
                        <Stack direction="row" justifyContent="space-between">
                            <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
                            {listFilterNode}
                        </Stack>
                    }
                    tableContainer={
                        <ClassTable
                            rootPermission={ROOT_PERMISSION}
                            tableContainerRef={tableContainerRef}
                            classes={listItems}
                            resource={resource}
                            searchFocused={searchFocused}
                            sortObjects={listFilter.sortObjects}
                            sort={applySort}
                            loadList={() => loadList(listFilter)}
                            listTotal={total}
                            selectId={selectId}
                            selectedIds={selectedIds}
                            setSelectedIds={setSelectedIds}
                            getIdsPromise={() => getIdsMethods()}
                            onBulkEdit={() => setIsBulkEditDialogOpen(true)}
                        />
                    }
                    footer={
                        <ListFooter
                            total={total}
                            size={listFilter.pageSize}
                            page={listFilter.page}
                            onPageChange={applyPage}
                            onSizeChange={applyPageSize}
                            onSortMethodChange={applySortMethod}
                        />
                    }
                />
            </Page>
            <ListContext.Provider
                value={{
                    loadList: (options) =>
                        loadList(listFilter, { resetPage: options?.resetPage }),
                    clearSelectedIds: () => setSelectedIds([]),
                }}
            >
                <ClassBulkEditPage
                    open={isBulkEditDialogOpen}
                    onClose={() => setIsBulkEditDialogOpen(false)}
                    selectedIds={selectedIds}
                />
                <Outlet />
            </ListContext.Provider>
        </>
    );
};

